/**
 * Utility for making tenant-aware API requests
 * Automatically includes tenant information in requests
 */

export interface TenantFetchOptions extends RequestInit {
  tenantId?: string
}

/**
 * Makes a fetch request with tenant context
 * Adds X-Tenant-ID header for public API endpoints
 * Adds X-Content-Version header for version-specific content (live/preview/last)
 */
export async function tenantFetch(
  url: string,
  options: TenantFetchOptions = {}
): Promise<Response> {
  const { tenantId, headers, ...fetchOptions } = options

  const requestHeaders = new Headers(headers)

  // Add tenant ID header if provided
  if (tenantId) {
    requestHeaders.set('X-Tenant-ID', tenantId)
  }

  // Add content version header if stored in sessionStorage (from branch deploys)
  if (typeof sessionStorage !== 'undefined') {
    const contentVersion = sessionStorage.getItem('content-version')
    if (contentVersion) {
      requestHeaders.set('X-Content-Version', contentVersion)
    }
  }

  // CRITICAL FIX: Add authentication token from localStorage
  // The token is stored by the auth system and needed for all authenticated requests
  if (typeof localStorage !== 'undefined') {
    const token = localStorage.getItem('auth-token')
    if (token) {
      requestHeaders.set('Authorization', `Bearer ${token}`)
      console.log('[tenantFetch] Added Authorization header from localStorage')
    } else {
      console.warn('[tenantFetch] No auth-token found in localStorage')
    }
  }

  return fetch(url, {
    ...fetchOptions,
    headers: requestHeaders,
    credentials: 'include' // Ensure cookies are sent for fallback auth
  })
}

/**
 * Creates a tenant-aware fetch function with a fixed tenant ID
 * Useful for creating a fetch function scoped to a specific tenant
 */
export function createTenantFetch(tenantId: string) {
  return (url: string, options: RequestInit = {}) => {
    return tenantFetch(url, { ...options, tenantId })
  }
}
